import { supabase } from '../supabase/client';
import { aktuelleZeile, type EinheitEigZeitraum } from '../eigentuemer/einheitEigentum';
import type { DbSnapshot, ImportPlan } from './types';
import { fuehreWechselAus } from '../wechsel/api';

export async function ladeSnapshot(objektnummern: string[]): Promise<DbSnapshot> {
  const gesucht = new Set(objektnummern.map((n) => n.trim()).filter(Boolean));
  const { data: alleObjekte } = await supabase.from('objekte').select('id, objektnummer, bezeichnung').eq('ist_verwaltung', false);
  const objekte = (alleObjekte || [])
    .filter((o: any) => o.objektnummer && gesucht.has(String(o.objektnummer).trim()))
    .map((o: any) => ({ id: o.id, objektnummer: String(o.objektnummer).trim(), bezeichnung: o.bezeichnung || '' }));
  const ids = objekte.map((o) => o.id);
  let einheiten: any[] = [], mieter: any[] = [], eigentuemer: any[] = [], einheitEigentuemer: any[] = [];
  if (ids.length) {
    const e = await supabase.from('objekt_einheiten').select('id, objekt_id, bezeichnung').in('objekt_id', ids);
    const m = await supabase.from('mieter')
      .select('id, objekt_id, vorname, nachname, firma_name, einheit_id, email, telefon, mobil, mietbeginn, mietende')
      .in('objekt_id', ids);
    einheiten = e.data || []; mieter = m.data || [];
    const oe = await supabase.from('objekt_eigentuemer').select('objekt_id, eigentuemer_id').in('objekt_id', ids);
    const eigIds = [...new Set((oe.data || []).map((r: any) => r.eigentuemer_id))];
    let eigRows: any[] = [];
    if (eigIds.length) {
      const er = await supabase.from('eigentuemer').select('id, vorname, nachname, firma_name').in('id', eigIds);
      eigRows = er.data || [];
    }
    const eigById = new Map(eigRows.map((e: any) => [e.id, e]));
    eigentuemer = (oe.data || []).map((r: any) => {
      const eg = eigById.get(r.eigentuemer_id) || { id: r.eigentuemer_id, vorname: null, nachname: null, firma_name: null };
      return { id: eg.id, objekt_id: r.objekt_id, vorname: eg.vorname, nachname: eg.nachname, firma_name: eg.firma_name };
    });
    const einheitIds = einheiten.map((x: any) => x.id);
    if (einheitIds.length) {
      const ee = await supabase.from('einheit_eigentuemer').select('einheit_id, eigentuemer_id').in('einheit_id', einheitIds);
      einheitEigentuemer = ee.data || [];
    }
  }
  return { objekte, einheiten, mieter, eigentuemer, einheitEigentuemer };
}

export async function schreibeBelegImport(plan: ImportPlan): Promise<{ einheiten: number; mieter: number; eigentuemer: number; kautionen: number; fehler: string[] }> {
  let eC = 0, mC = 0, egC = 0, kC = 0;
  const fehler: string[] = [];
  const heute = new Date().toISOString().slice(0, 10);
  const eigentuemerWechselIds: string[] = [];
  for (const d of plan.dateien) {
    if (!d.objekt_id || d.fehler) continue;
    const idByBez = new Map<string, string>();
    for (const ea of d.einheiten) {
      try {
        if (ea.aktion === 'aktualisieren' && ea.einheitId) {
          const patch: any = { typ: ea.typ };
          if (ea.notizen) patch.notizen = ea.notizen;
          if (ea.wohnflaeche != null) patch.wohnflaeche = ea.wohnflaeche;
          if (ea.nutzflaeche != null) patch.nutzflaeche = ea.nutzflaeche;
          if (ea.zimmer != null) patch.zimmer = ea.zimmer;
          const { error } = await supabase.from('objekt_einheiten').update(patch).eq('id', ea.einheitId);
          if (error) throw error;
          idByBez.set(ea.bezeichnung, ea.einheitId);
        } else {
          const insert: any = { objekt_id: ea.objekt_id, bezeichnung: ea.bezeichnung, typ: ea.typ, notizen: ea.notizen };
          if (ea.wohnflaeche != null) insert.wohnflaeche = ea.wohnflaeche;
          if (ea.nutzflaeche != null) insert.nutzflaeche = ea.nutzflaeche;
          if (ea.zimmer != null) insert.zimmer = ea.zimmer;
          const { data, error } = await supabase.from('objekt_einheiten').insert(insert).select('id').single();
          if (error) throw error;
          idByBez.set(ea.bezeichnung, data!.id); eC++;
        }
      } catch (e: any) { fehler.push(`${d.dateiname} / Einheit ${ea.bezeichnung}: ${e?.message || e}`); }
    }
    for (const ma of d.mieter) {
      try {
        if (ma.aktion === 'uebersprungen') continue;
        const einheit_id = idByBez.get(ma.einheitBezeichnung) || null;
        if (ma.aktion === 'aktualisieren' && ma.mieterId) {
          const { error } = await supabase.from('mieter').update({ ...ma.felder, einheit: ma.einheitBezeichnung, einheit_id, status: 'aktiv', updated_at: new Date().toISOString() }).eq('id', ma.mieterId);
          if (error) throw error;
        } else if (ma.aktion === 'wechsel') {
          if (ma.alterMieterId) {
            const { error } = await supabase.from('mieter').update({ einheit_id: null, einheit: null, status: 'inaktiv' }).eq('id', ma.alterMieterId);
            if (error) throw error;
          }
          const { error } = await supabase.from('mieter').insert({ ...ma.neu, objekt_id: ma.objekt_id, einheit_id, einheit: ma.einheitBezeichnung, status: 'aktiv' });
          if (error) throw error; mC++;
        } else if (ma.aktion === 'neu') {
          const { error } = await supabase.from('mieter').insert({ ...ma.neu, objekt_id: ma.objekt_id, einheit_id, einheit: ma.einheitBezeichnung, status: 'aktiv' });
          if (error) throw error; mC++;
        }
      } catch (e: any) { fehler.push(`${d.dateiname} / Mieter ${ma.anzeigeName}: ${e?.message || e}`); }
    }
    for (const ea of d.eigentuemer) {
      try {
        const einheit_id = idByBez.get(ea.einheitBezeichnung);
        if (!einheit_id) throw new Error('Einheit nicht aufgelöst');
        let eigId = ea.eigentuemerId;
        if (!eigId && ea.neu) {
          const { data, error } = await supabase.from('eigentuemer')
            .insert({ vorname: ea.neu.vorname || null, nachname: ea.neu.nachname, firma_name: ea.neu.firma_name, status: 'aktiv' })
            .select('id').single();
          if (error) throw error; eigId = data!.id; egC++;
        }
        if (!eigId) throw new Error('Kein Eigentümer');
        const { data: vorhanden } = await supabase.from('objekt_eigentuemer')
          .select('objekt_id').eq('objekt_id', ea.objekt_id).eq('eigentuemer_id', eigId).maybeSingle();
        if (!vorhanden) {
          const { error } = await supabase.from('objekt_eigentuemer').insert({ objekt_id: ea.objekt_id, eigentuemer_id: eigId });
          if (error) throw error;
        }
        // Historie je Einheit: laufender Eigentümer vor dem Import; Zeile je Eigentümer und Einheit
        const { data: einheitZeilen, error: eeLadeErr } = await supabase.from('einheit_eigentuemer')
          .select('eigentuemer_id, mea, hausgeld_vorschuss, vertrag_ab, vertrag_bis').eq('einheit_id', einheit_id);
        if (eeLadeErr) throw eeLadeErr;
        const bestehendeZuordnung = aktuelleZeile((einheitZeilen ?? []) as ({ eigentuemer_id: string } & EinheitEigZeitraum)[], heute);
        // vertrag_bis des Vorbesitzers (Vortag) setzt die Ausführung des Ordnerwechsels unten
        const { error: eeErr } = await supabase.from('einheit_eigentuemer')
          .upsert({ einheit_id, eigentuemer_id: eigId, ...ea.weg, updated_at: new Date().toISOString() }, { onConflict: 'einheit_id,eigentuemer_id' });
        if (eeErr) throw eeErr;
        if (bestehendeZuordnung && bestehendeZuordnung.eigentuemer_id !== eigId) {
          try {
            const { data: wechselRow, error: wechselErr } = await supabase.from('ordner_wechsel')
              .insert({
                typ: 'eigentuemer_einheit', objekt_id: ea.objekt_id, einheit_id,
                alt_ref_id: bestehendeZuordnung.eigentuemer_id, neu_ref_id: eigId, faellig_am: heute,
              })
              .select('id').single();
            if (wechselErr) throw wechselErr;
            if (wechselRow?.id) eigentuemerWechselIds.push(wechselRow.id);
          } catch (wechselE: any) {
            console.error(`Ordner-Wechsel-Planung fehlgeschlagen (Eigentümer ${ea.anzeigeName}):`, wechselE?.message || wechselE);
          }
        }
      } catch (e: any) { fehler.push(`${d.dateiname} / Eigentümer ${ea.anzeigeName}: ${e?.message || e}`); }
    }
    for (const ka of d.kautionen) {
      try {
        if (ka.aktion === 'uebersprungen' || !ka.mieterId) continue;
        const { error } = await supabase.from('mieter').update({ ...ka.felder, updated_at: new Date().toISOString() }).eq('id', ka.mieterId);
        if (error) throw error; kC++;
      } catch (e: any) { fehler.push(`${d.dateiname} / Kaution ${ka.anzeigeName}: ${e?.message || e}`); }
    }
  }
  if (eigentuemerWechselIds.length) {
    fuehreWechselAus(eigentuemerWechselIds).catch((e) => console.error('Eigentuemer-Wechsel-Ausfuehrung fehlgeschlagen (Cron holt nach):', e));
  }
  return { einheiten: eC, mieter: mC, eigentuemer: egC, kautionen: kC, fehler };
}
